import type { Product, ProductVariant } from '@/lib/products';
import { absoluteUrl, productImageUrls, SITE_NAME, SITE_URL } from '@/lib/seo';

// Google product category: Food, Beverages & Tobacco > Beverages > Coffee
const COFFEE_CATEGORY_ID = '1868';

type FeedCurrency = 'dkk' | 'eur';

export interface FeedShipping {
  /** ISO country code, e.g. "DK". */
  country: string;
  /** Standard shipping price in minor units (øre/cents) for the feed currency. */
  price: number;
  /** Optional order subtotal (minor units) at or above which shipping is free. */
  freeAboveSubtotal?: number;
}

export interface FeedOptions {
  currency?: FeedCurrency;
  /** One entry per destination country to advertise shipping for. */
  shipping?: FeedShipping[];
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function formatFeedPrice(minorUnits: number, currency: FeedCurrency): string {
  return `${(minorUnits / 100).toFixed(2)} ${currency.toUpperCase()}`;
}

function variantPrice(variant: ProductVariant, currency: FeedCurrency): number {
  return currency === 'eur' ? variant.priceEur : variant.price;
}

/**
 * Stable, URL-safe variant id used as the Merchant Center `g:id`,
 * e.g. "etiopien-yirgacheffe-250-gram".
 *
 * This is the canonical product identifier. GA4 ecommerce events MUST use the
 * exact same value as `item_id` so Google Merchant Center can attribute
 * purchases to the matching feed item.
 */
export function feedItemId(slug: string, size: string): string {
  const sizeSlug = size
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
  return `${slug}-${sizeSlug || 'std'}`;
}

function variantId(product: Product, variant: ProductVariant): string {
  return feedItemId(product.slug, variant.size);
}

/** Parse "250 gram" -> "250 g" for unit pricing; null when not a gram weight. */
function unitPricingMeasure(size: string): string | null {
  const match = size.match(/(\d+(?:[.,]\d+)?)\s*(g|gram|kg|ml|l)?/i);
  if (!match) return null;
  const amount = match[1].replace(',', '.');
  const rawUnit = (match[2] || 'g').toLowerCase();
  const unit = rawUnit === 'gram' ? 'g' : rawUnit;
  return `${amount} ${unit}`;
}

function tag(name: string, value: string): string {
  return `      <${name}>${escapeXml(value)}</${name}>`;
}

function buildShipping(rate: FeedShipping, currency: FeedCurrency, itemPrice: number): string[] {
  const free = typeof rate.freeAboveSubtotal === 'number' && itemPrice >= rate.freeAboveSubtotal;
  const shippingPrice = free ? 0 : rate.price;
  return [
    '      <g:shipping>',
    `        <g:country>${escapeXml(rate.country)}</g:country>`,
    `        <g:price>${escapeXml(formatFeedPrice(shippingPrice, currency))}</g:price>`,
    '      </g:shipping>',
  ];
}

function buildItem(
  product: Product,
  variant: ProductVariant,
  currency: FeedCurrency,
  shipping?: FeedShipping[]
): string | null {
  const price = variantPrice(variant, currency);
  if (!Number.isFinite(price) || price <= 0) return null;

  const images = productImageUrls(product);
  const imageLink = images[0];
  if (!imageLink) return null;

  const link = absoluteUrl(`/shop/${product.slug}`);
  const title = `${product.name} – ${variant.size}`.replace(/\s+/g, ' ').trim();
  const description =
    product.description?.trim() ||
    [product.name, product.flavor, product.origin].filter(Boolean).join(' · ') ||
    product.name;

  const lines: string[] = [
    '    <item>',
    tag('g:id', variantId(product, variant)),
    tag('g:item_group_id', product.slug),
    tag('g:title', title),
    tag('g:description', description),
    tag('g:link', link),
    tag('g:image_link', imageLink),
    tag('g:availability', 'in_stock'),
    tag('g:condition', 'new'),
    tag('g:price', formatFeedPrice(price, currency)),
    tag('g:brand', SITE_NAME),
    tag('g:identifier_exists', 'no'),
    tag('g:google_product_category', COFFEE_CATEGORY_ID),
  ];

  const productType = [product.origin, product.roast].filter((v) => v && v.trim()).join(' > ');
  if (productType) lines.push(tag('g:product_type', productType));

  const measure = unitPricingMeasure(variant.size);
  if (measure) lines.push(tag('g:unit_pricing_measure', measure));

  // Up to 10 extra images supported by Google.
  images.slice(1, 11).forEach((img) => lines.push(tag('g:additional_image_link', img)));

  shipping?.forEach((rate) => lines.push(...buildShipping(rate, currency, price)));

  lines.push('    </item>');
  return lines.join('\n');
}

/**
 * Build a Google Merchant Center product feed (RSS 2.0 with the `g:` namespace).
 * Each product variant (size) is emitted as its own item sharing an `item_group_id`.
 */
export function buildGoogleMerchantFeed(products: Product[], options: FeedOptions = {}): string {
  const currency = options.currency ?? 'dkk';
  const items: string[] = [];

  for (const product of products) {
    if (product.visible === false) continue;
    for (const variant of product.variants ?? []) {
      const item = buildItem(product, variant, currency, options.shipping);
      if (item) items.push(item);
    }
  }

  return `<?xml version="1.0" encoding="UTF-8"?>
<rss version="2.0" xmlns:g="http://base.google.com/ns/1.0">
  <channel>
    <title>${escapeXml(SITE_NAME)}</title>
    <link>${escapeXml(SITE_URL)}</link>
    <description>${escapeXml(`${SITE_NAME} produktfeed til Google Merchant Center`)}</description>
${items.join('\n')}
  </channel>
</rss>
`;
}
